import { useSyncExternalStore } from 'react';
import { isStandalone } from '../lib/installPlatform';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * The browser's offer to install, caught once for the whole app.
 *
 * Chromium fires `beforeinstallprompt` a single time, early in the page's
 * life. It used to be caught inside a hook, so only a component mounted
 * before it fired ever saw it; Settings mounts later, called the hook
 * again, and its Install button never appeared. `startInstallCapture` runs
 * from main.tsx before anything renders and keeps the event here for
 * whichever page wants it.
 */

let offer: BeforeInstallPromptEvent | null = null;
let installedNow = false;
const listeners = new Set<() => void>();

function announce() {
  for (const listener of listeners) listener();
}

export function startInstallCapture(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Held, so the app shows its own button instead of the browser's bar.
    event.preventDefault();
    offer = event as BeforeInstallPromptEvent;
    announce();
  });
  window.addEventListener('appinstalled', () => {
    offer = null;
    installedNow = true;
    announce();
  });
}

export interface InstallState {
  /** The browser will show its install dialog when asked. */
  canPrompt: boolean;
  /** Already running as the installed app, or just installed. */
  installed: boolean;
}

let snapshot: InstallState = { canPrompt: false, installed: false };

function read(): InstallState {
  const next = {
    canPrompt: offer !== null,
    installed: installedNow || isStandalone(window),
  };
  if (
    next.canPrompt !== snapshot.canPrompt ||
    next.installed !== snapshot.installed
  ) {
    snapshot = next;
  }
  return snapshot;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useInstall(): InstallState & { prompt: () => Promise<void> } {
  const state = useSyncExternalStore(subscribe, read);
  return { ...state, prompt: promptInstall };
}

export async function promptInstall(): Promise<void> {
  if (!offer) return;
  const held = offer;
  await held.prompt();
  const { outcome } = await held.userChoice;
  // The event is single use whatever the answer.
  offer = null;
  if (outcome === 'accepted') installedNow = true;
  announce();
}
